import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MapPinOff } from "lucide-react";
import { toast } from "sonner";
import { getStudentsMissingDistrict, getDistricts, updateStudent } from "../lib/api";

/** Admin Dashboard nudge: current students with no Sending School / District on file.
 * Expands to a list with an inline district picker to fix each in place. Hidden when none. */
export default function MissingDistrictCard() {
  const [students, setStudents] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState({});
  const [picked, setPicked] = useState({});

  const load = async () => {
    try {
      const r = await getStudentsMissingDistrict();
      setStudents(r.data.students || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    load();
    getDistricts().then((r) => setDistricts([...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
  }, []);

  const assign = async (sid) => {
    const name = picked[sid];
    if (!name) return;
    setSaving((p) => ({ ...p, [sid]: true }));
    try {
      await updateStudent(sid, { school_district: name });
      toast.success("Sending school set");
      await load();
    } catch {
      toast.error("Could not set the sending school");
    } finally {
      setSaving((p) => ({ ...p, [sid]: false }));
    }
  };

  if (!students.length) return null;

  return (
    <Card className="animate-fade-in border-amber-300 bg-amber-50" data-testid="missing-district-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-amber-900">
          <MapPinOff className="w-4 h-4" />
          {students.length} student{students.length !== 1 ? "s" : ""} need a sending school
        </CardTitle>
        <CardDescription className="text-amber-800">
          These active students have no Sending School / District on file. Set it so billing, letters and exports are correct.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100"
          onClick={() => setOpen((o) => !o)} data-testid="missing-district-toggle">
          {open ? "Hide list" : "Set sending schools"}
        </Button>
        {open && (
          <ul className="mt-3 space-y-2" data-testid="missing-district-list">
            {students.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 bg-white border border-amber-200 rounded-lg p-2" data-testid={`missing-district-row-${s.id}`}>
                <span className="min-w-0 text-sm">
                  <span className="font-semibold text-slate-900">{s.name}</span>
                  {s.grade ? <span className="text-xs text-slate-500 ml-2">Grade {s.grade}</span> : null}
                </span>
                <div className="flex items-center gap-2">
                  <Select value={picked[s.id] || ""} onValueChange={(v) => setPicked((p) => ({ ...p, [s.id]: v }))}>
                    <SelectTrigger className="h-8 w-[180px]" data-testid={`missing-district-select-${s.id}`}>
                      <SelectValue placeholder="Choose district" />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" disabled={!picked[s.id] || saving[s.id]}
                    onClick={() => assign(s.id)} data-testid={`missing-district-save-${s.id}`}>
                    {saving[s.id] ? "…" : "Set"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
